// ---------------------------------------------------------------------------
// The admin sign-in form.
//
// Split out of `AdminScreen.tsx`. It is on its own because it is the one piece
// of the screen that runs before there is a session, and the only one that ever
// touches the private key: it signs the challenge in the tab, hands back a
// session token, and clears the field. Nothing else in `admin/` imports
// `adminLogin`, and nothing else should.
// ---------------------------------------------------------------------------

import { useState } from 'react';
import { adminLogin } from '../../lib/adminAuth.js';
import { Button, Card, CardTitle } from '../../components/primitives/index.js';

export function SignIn({ onSignedIn }: { onSignedIn: (token: string) => void }) {
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  return (
    <div className="mt-5">
      <Card>
        <CardTitle>Sign in</CardTitle>
        <p className="mt-2 max-w-[520px] text-sm text-ink-muted text-pretty">
          Paste the Ed25519 private key. It signs the server's challenge in
          this tab and is never stored or sent. Only the session token persists.
        </p>
        <form
          className="mt-3 flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            setBusy(true);
            setFailure(null);
            adminLogin(key)
              .then((session) => {
                setKey('');
                onSignedIn(session.token);
              })
              .catch((thrown: Error) => setFailure(thrown.message))
              .finally(() => setBusy(false));
          }}
        >
          <label className="flex flex-col gap-1.5">
            <span className="label-mono">private key</span>
            <textarea
              value={key}
              onChange={(event) => setKey(event.target.value)}
              rows={3}
              autoComplete="off"
              spellCheck={false}
              className="min-h-11 w-full rounded-card border border-line bg-surface-2 px-3 py-2 font-mono text-[16px] text-ink"
            />
          </label>
          {failure && (
            <p role="alert" className="text-sm text-alert">
              {failure}
            </p>
          )}
          <Button type="submit" disabled={busy || key.trim().length === 0}>
            {busy ? 'Checking' : 'Sign in'}
          </Button>
        </form>
      </Card>
    </div>
  );
}
